<template>
    <div class="container--large-content">
        <template v-if="isPageReady">
            <template v-if="!groups">
                <div class="container">
                    <h1 class="page__title">{{ $t("empty-circles-match-message") }}</h1>

                    <router-link
                        to="/circles"
                        class="button button--primary">{{ $t("add-a-profile") }}</router-link>
                </div>
            </template>
            <template v-else>
                <template v-if="!currentDataId">
                    <div class="container">
                        <h1>{{ $t('click-ranking-title') }}</h1>

                        <router-link
                            class="button button--primary"
                            to="/circles">
                            {{ $t('add-a-profile') }}
                        </router-link>
                    </div>

                </template>
                <template v-else>
                    <div class="container">
                        <div class="ranking-header">

                            <h1 class="page__title">{{ $t("your-circle-x", {x: $t(getGroupTypeName(currentGroupType))}) }}</h1>
                            <ul class="sub-navigation">
                                <li
                                    v-for="(option, index) in currentTypeOptions"
                                    :key="`options-type-${index}`"
                                    class="sub-navigation__item">
                                    <button
                                        @click="updateCurrentType(option.value)"
                                        type="button"
                                        class="button--transparent sub-navigation__button"
                                        :class="{'active': option.value === currentType}">
                                        <img
                                            src=""
                                            alt=""
                                            class="sub-navigation__button-icon" />
                                        <span class="sub-navigation__button-label">
                                            {{ option.label }}
                                        </span>
                                    </button>
                                </li>
                            </ul>
                            <div class="toolbar">
                                <div
                                    v-if="subTitles || currentHelpContent"
                                    class="page__subtitle"
                                    :class="{'page__subtitle--attraction': (currentType === 'attraction' || currentType === 'reversed-attraction')}">

                                    <div class="grid grid--centered">
                                        <div class="grid__item desktop--8">
                                            <p
                                                v-for="(subTitle, key) in subTitles"
                                                :key="`subtitle-${key}`"
                                                v-html="subTitle"></p>
                                            <template v-if="currentType === 'attraction'">
                                                <button
                                                    class="button button--primary button--subtitle-attraction"
                                                    @click="updateCurrentType('reversed-attraction')">
                                                    {{ $t("vice-versa") }}
                                                </button>
                                            </template>
                                            <template v-if="currentType === 'reversed-attraction'">
                                                <button
                                                    class="button button--primary button--subtitle-attraction"
                                                    @click="updateCurrentType('attraction')">
                                                    {{ $t("vice-versa") }}
                                                </button>
                                            </template>
                                        </div>
                                        <div
                                            v-if="currentHelpContent"
                                            class="grid__item desktop--4 toolbar__extra-tools">
                                            <div
                                                id="toolBarExtraToolsWrapper">
                                                <button
                                                    v-if="!isMobile"
                                                    @click="toggleHelpModal"
                                                    type="button"
                                                    class="button button--secondary">
                                                    {{ $t("help") }}
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    </div>

                    <div
                        class="fullwidth-wrapper"
                        :class="{'fullwidth-wrapper--match': currentType !== 'who-are-they'}">

                        <template v-if="currentType !== 'who-are-they'">
                            <template v-if="isTableReady">
                                <template
                                    v-if="currentType === 'resemblance' || currentType === 'reversed-attraction'">
                                    <match-traits-table
                                        :group-traits="groupTraits"
                                        :current-user="loggedUser"
                                        :current-type="currentType"
                                        @update-subtitle="setSubtitles" />
                                </template>
                                <template v-else-if="currentType === 'adaptation' || currentType === 'popularity' || currentType === 'opponent'">
                                    <match-ranking-table
                                        :group-traits="groupTraits"
                                        :current-type="currentType"
                                        @update-subtitle="setSubtitles" />
                                </template>
                                <template v-else-if="currentType === 'attraction' || currentType === 'reversed-attraction'">
                                    <match-traits-table
                                        :group-traits="groupTraits"
                                        :current-user="loggedUser"
                                        :current-type="currentType"
                                        @update-subtitle="setSubtitles" />
                                </template>
                            </template>
                            <template v-else>
                                <div
                                    v-if="fetchInsightTraitsTentative <= 5"
                                    class="loader">
                                    <HorizontalBubbleLoader />
                                </div>
                                <div v-else>
                                    <div class="container">
                                        <p>{{ $t("service-temporarily-unavailable") }}</p>
                                    </div>
                                </div>
                            </template>
                        </template>

                        <template v-else>
                            <who-are-they
                                :group-id="parseInt(currentDataId)" />
                        </template>
                    </div>
                </template>
            </template>

            <help-modal
                v-if="isHelpModalVisible"
                :data="currentHelpContent"
                @closeModal="closeHelpModal">
            </help-modal>
        </template>
        <template v-else>
            <HorizontalBubbleLoader />
        </template>

    </div>
</template>

<script>
// @ is an alias to /src
import Vue from "vue";
import axios from "axios";


import { getMeCircle } from "@/utils/api/usersApi.js";
import { userGroupInfo } from "@/utils/api/groupsApi.js";

import { getGroupTypeName, getGroupTypeColor } from "@/utils/helpers/group.js";

import MatchTraitsTable from "@/components/charts/MatchTraitsTable.vue";
import MatchRankingTable from "@/components/charts/MatchRankingTable.vue";
import WhoAreThey from "@/components/in-depth/WhoAreThey.vue";
import HelpModal from "@/components/modals/HelpModal.vue";


import HorizontalBubbleLoader from "@/components/loaders/HorizontalBubbleLoader.vue";

export default {
    name: "InDepth",
    components: {
        "MatchTraitsTable": MatchTraitsTable,
        "MatchRankingTable": MatchRankingTable,
        "WhoAreThey": WhoAreThey,
        "HelpModal": HelpModal,
        "HorizontalBubbleLoader": HorizontalBubbleLoader,
    },
    data() {
        return {
            helpData: null,
            isPageReady: false,
            currentGroupId: null,
            currentGroupType: 1,
            currentType: "who-are-they",
            fetchInsightTraitsTentative: 0,
            isMobile: false,
            subTitles: null,
            userInspected: null
        };
    },
    computed: {
        // currentDataId
        // Current data (user of group) id to display
        currentDataId() {
            return this.$store.getters["globalModule/getCurrentDataId"];
        },
        isTableReady() {
            if (this.groupTraits) {
                return true;
            }

            return false;
        },
        // loggedUser
        // Current logged user
        loggedUser() {
            return this.$store.getters["userModule/getUser"];
        },
        // groups
        // User groups info
        groups() {
            return this.$store.getters["userModule/getGroups"];
        },
        // groupBubbleData
        // Formated data match what is expected by Bubblegraph component
        groupBubbleData() {
            if (this.groups) {

                const formatedGroups = this.groups.map((item) => {

                    const group = {
                        "traits": this.$t(getGroupTypeName(item.id_group_type)),
                        "color": getGroupTypeColor(item.id_group_type),
                        "id_person": item.id_person,
                        "id_group": item.id_group,
                        "type": "QQ",
                        "points_perc": 100 / this.groups.length,
                        "category": "group",
                    };

                    return group;
                });

                return formatedGroups;
            }

            return;
        },
        groupUsers() {

            const valueIndex = this.getObjetIndexByKey(this.groups, "id_group", String(this.currentDataId));

            const currentGroup = this.groups[valueIndex];

            const currentGroupUsers = currentGroup.id_person_2;

            let members = [];

            if (currentGroup && currentGroup.id_person_2) {

                const membersIdArray = currentGroup.id_person_2.split(",");
                const membersNameArray = currentGroup.name.split(",");

                for (var i = membersIdArray.length - 1; i >= 0; i--) {

                    const member = {
                        id: membersIdArray[i],
                        name: membersNameArray[i]
                    };

                    members.push(member);
                }
            }

            const sorteArray = members.sort(function(a, b) {
                var nameA = a.name.toUpperCase(); // ignore upper and lowercase
                var nameB = b.name.toUpperCase(); // ignore upper and lowercase

                if (nameA < nameB) {
                    return -1; //nameA comes first
                }

                if (nameA > nameB) {
                    return 1; // nameB comes first
                }

                return 0;  // names must be equal
            });

            return sorteArray;

        },
        groupTraits() {
            if (this.insightTraits) {
                const traits = this.insightTraits.filter((trait) => {
                    if (trait.id_group !== parseInt(this.currentDataId)) {
                        return false;
                    }

                    return true;
                });

                return traits;
            }
        },
        // groupsSelectOptions
        // Specific maping of group to display the input select group
        groupsSelectOptions() {
            if (this.groups) {

                const selectOptions = this.groups.map((group) => {
                    const option = {
                        "value": group.id_group,
                        "label": this.$t(getGroupTypeName(group.id_group_type))
                    };

                    return option;
                });

                return selectOptions;
            }

            return;
        },
        groupUsersOption() {
            return this.groupUsers.map((item) => {
                return {
                    "value": item.id,
                    "label": item.name
                };
            });
        },
        currentTypeOptions() {
            const options = [
                {"value": "who-are-they", "label": this.$t("who-are-they")},
                {"value": "resemblance", "label": this.$t("resemblance")},
                {"value": "adaptation", "label": this.$t("adaptation")},
                {"value": "attraction", "label": this.$t("attraction")},
                // {"value": "reversed-attraction", "label": this.$t("reversed-attracted-to")},
            ];

            const loveGroupIndex = this.getObjetIndexByKey(this.groups, "id_group_type", "1");
            const friendGroupIndex = this.getObjetIndexByKey(this.groups, "id_group_type", "2");
            const rivalGroupIndex = this.getObjetIndexByKey(this.groups, "id_group_type", "6");

            // If groupType is love
            if (this.currentGroupType === 1) {
                options.splice(4, 0, {"value": "popularity", "label": this.$t("popularity")});
            }

            // If groupType is Rival / Friends
            if (this.currentGroupType === 2 || this.currentGroupType === 6) {
                if (this.currentGroupType === 2) {
                    if (loveGroupIndex !== -1 && friendGroupIndex !== -1) {
                        options.splice(4, 0, {"value": "opponent", "label": this.$t("opponent")});
                    }
                }

                if (this.currentGroupType === 6) {
                    if (loveGroupIndex !== -1 && rivalGroupIndex !== -1) {
                        options.splice(4, 0, {"value": "opponent", "label": this.$t("opponent")});
                    }
                }
            }

            return options;
        },
        // friendsTraits
        // All traits save in store (if an initial call was successfull)
        insightTraits() {
            return this.$store.getters["userModule/getInsightTraits"];
        },
        isHelpModalVisible() {
            return this.$store.getters["modalModule/isMatchHelpModalVisible"];
        },
        currentHelpContent() {
            if (this.helpData) {
                switch (this.currentType) {
                case "popularity":
                    return this.helpData["popularity-help"];
                case "adaptation":
                    return this.helpData["adaptation-help"];
                case "attraction":
                    return this.helpData["attracted-to-help"];
                case "revsersed-attraction":
                    return this.helpData["reversed-attracted-to-help"];
                case "resemblance":
                    return this.helpData["same-help"];
                default:
                    return this.helpData["match-search"];
                }
            }

            return;
        },
    },
    watch: {
        currentType: {
            handler(val) {
                switch (val) {
                case "who-are-they":
                    this.subTitles = null;
                    break;
                }
            },
            immediate: true
        },
        currentDataId(val, oldVal) {
            if (val !== oldVal) {
                this.updateCurrentGroup(this.currentDataId);
            }
        },
        currentGroupType(val) {
            if (this.currentType === "popularity") {
                if (val !== 1) {
                    this.currentType = "who-are-they";
                }
            }

            if (this.currentType === "opponent") {
                if (val !== 2 && val !== 6) {
                    this.currentType = "who-are-they";
                }
            }
        }
    },
    mounted() {
        // Responsive Behavior
        /* istanbul ignore next */
        this.$responsive.registerMediaQuery("to-medium", () => {
            this.isMobile = true;
        }, () => {
            this.isMobile = false;
        });

        this.fetchHelpModalContent();

        // User Groups
        if (!this.groups) {
            this.fetchGroups();
        } else {
            this.buildGroupSelectionDataOptions();

            // Insight Traits
            if (!this.insightTraits) {
                this.fetchInsightTraits();
            }

            this.isPageReady = true;
        }
    },
    methods: {
        toggleHelpModal() {
            if (this.isHelpModalVisible) {
                this.closeHelpModal();
            } else {
                this.openHelpModal();
            }
        },
        openHelpModal() {
            this.$store.commit("modalModule/setVisibilityMatchHelpModal", true);
        },
        closeHelpModal() {
            this.$store.commit("modalModule/setVisibilityMatchHelpModal", false);
        },
        updateCurrentType(value) {
            this.currentType = value;
        },
        updateCurrentGroup(groupId) {
            if (this.groups) {
                this.currentGroupId = groupId;

                const groupIndex = this.getObjetIndexByKey(this.groups, "id_group", groupId);

                if (groupIndex !== -1) {
                    this.currentGroupType = parseInt(this.groups[groupIndex].id_group_type);
                }
            }
        },
        // fetch User Insight Traits
        fetchInsightTraits() {
            this.fetchInsightTraitsTentative += 1;

            const user = {
                "id": this.loggedUser.id_person
            };

            getMeCircle(user).then((res) => {
                if (res.status === 200 && res.data) {
                    this.$store.commit("userModule/setInsightTraits", res.data);
                } else {
                    // If res.data is empty, it maybe bcause the API is still processing data (after a getMeCircleFirstTime call), try again to fetch data.
                    if (this.fetchInsightTraitsTentative <= 5 && (!res.data || (res.data && res.data.length === 0))) {
                        this.fetchInsightTraits();
                    } else {
                        // Handle Error
                        let message = this.$t("a-server-error-occured-please-try-later");
                        this.$notificationsBar.alert(message, {type: "warning"});
                        this.resetCurrentGroup();
                    }
                }
            });
        },
        // Fetch User Groups Info
        fetchGroups() {
            const user = {
                "id": this.loggedUser.id_person
            };

            userGroupInfo(user).then((res) => {
                if (res.status === 200 && res.data) {
                    this.isPageReady = true;
                    if (res.data.message === "no group for user") {
                        return;
                    } else {
                        this.$store.commit("userModule/setGroups", res.data);

                        this.buildGroupSelectionDataOptions();

                    }
                }
            });
        },
        buildGroupSelectionDataOptions() {
            if (this.groups) {
                let datasOptions = [];

                this.groups.forEach((group) => {

                    datasOptions.push({
                        "value": group.id_group,
                        "label": this.$t(getGroupTypeName(group.id_group_type))
                    });
                });

                if (datasOptions.length > 0) {
                    this.$store.commit("globalModule/setAvailableDatas", datasOptions);
                }

                // Insight Traits
                if (!this.insightTraits) {
                    this.fetchInsightTraits();
                }

                this.updateCurrentGroup(this.groups[0].id_group);
            }
        },
        fetchHelpModalContent() {
            const currentLocal = this.$i18n.locale;

            axios({
                url: `/json/help/help-match.${currentLocal}.json`,
            }).then(res => {
                this.helpData = res.data;
            }).catch(e => {
                if (e.response) {
                    throw e.response;
                }
            });
        },
        setSubtitles(subtitles) {
            this.subTitles = subtitles;
        },
        resetCurrentGroup() {
            this.currentGroupType = null;
            this.currentGroupId = null;
        }
    }
};
</script>
